// src/context/PortalContext.tsx

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react'
import axios from 'axios'
import config from '../config'
import { useAuth } from './AuthContext'

// Venue interfaces
export interface Venue {
  id: string
  name: string
  city: string
  state: string
  country: string
  internal_id?: string
  qr_code_svg_url?: string
  qr_code_png_url?: string
  onboard_url?: string
  created_at: string
}

export interface Location {
  id: string
  venue_id: string
  label: string
  qr_code_svg_url?: string
  qr_code_png_url?: string
  onboard_url?: string
  created_at: string
}

// Create/Update request interfaces
export interface CreateVenueRequest {
  name: string
  city: string
  state: string
  country: string
  internal_id?: string | null
  user_id: string
}

export interface CreateLocationRequest {
  label: string
  venue_id: string
}

interface PortalContextType {
  // Venue management
  venues: Venue[]
  locations: Location[]
  isLoading: boolean
  error: string | null
  
  // Venue API methods
  loadVenues: () => Promise<void>
  createVenue: (venue: CreateVenueRequest) => Promise<Venue | null>
  updateVenue: (venueId: string, updates: Partial<CreateVenueRequest>) => Promise<Venue | null>
  deleteVenue: (venueId: string) => Promise<void>
  
  // Location API methods
  createLocation: (location: CreateLocationRequest) => Promise<Location | null>
  updateLocation: (locationId: string, updates: Partial<CreateLocationRequest>) => Promise<Location | null>
  deleteLocation: (locationId: string) => Promise<void>
  
  // OAuth API methods
  createOAuthClient: (clientData: {
    name: string;
    description: string;
    redirect_uris: string[];
    allowed_domains: string[];
    scopes: string[];
  }) => Promise<any | null>;
  
  // Bulk venue operations
  validateCSV: (csvData: any[]) => Promise<any>;
  bulkVenues: (venues: any[], action: 'add' | 'replace' | 'delete') => Promise<any>;
  
  // Utility methods
  getLocationsForVenue: (venueId: string) => Location[]
  clearError: () => void
}

const PortalContext = createContext<PortalContextType | null>(null)

export const usePortal = (): PortalContextType => {
  const context = useContext(PortalContext)
  if (!context) {
    throw new Error('usePortal must be used within a PortalProvider')
  }
  return context
}

interface PortalProviderProps {
  children: ReactNode
}

export const PortalProvider: React.FC<PortalProviderProps> = ({ children }) => {
  const { user } = useAuth()
  const [venues, setVenues] = useState<Venue[]>([])
  const [locations, setLocations] = useState<Location[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)


  // Load all venues and locations
  const loadVenues = useCallback(async () => {
    if (!user) return
    
    try {
      setIsLoading(true)
      setError(null)
      
      // Load venues
      const venuesResponse = await axios.get(`${config.API_BASE_URL}/api/venues`)
      setVenues(venuesResponse.data.items || [])
      
      // Load locations
      const locationsResponse = await axios.get(`${config.API_BASE_URL}/api/locations`)
      setLocations(locationsResponse.data.items || [])
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsLoading(false)
    }
  }, [user])

  // Create a new venue
  const createVenue = useCallback(async (venue: CreateVenueRequest): Promise<Venue | null> => {
    if (!user) return null
    
    try {
      const { data } = await axios.post<Venue>(
        `${config.API_BASE_URL}/api/venues`,
        venue
      )
      
      // Refresh venues list
      await loadVenues()
      return data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create venue')
      return null
    }
  }, [user, loadVenues])

  // Create OAuth client
  const createOAuthClient = useCallback(async (clientData: {
    name: string;
    description: string;
    redirect_uris: string[];
    allowed_domains: string[];
    scopes: string[];
  }) => {
    if (!user) return null;
    
    try {
      const { data } = await axios.post(
        `${config.API_BASE_URL}/oauth/portal/register-json`,
        clientData
      );
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create OAuth client');
      return null;
    }
  }, [user]);

  // Validate CSV structure
  const validateCSV = useCallback(async (csvData: any[]) => {
    try {
      const { data } = await axios.post(
        `${config.API_BASE_URL}/api/validate-csv`,
        csvData
      );
      return data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to validate CSV';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  // Bulk venue operations
  const bulkVenues = useCallback(async (venues: any[], action: 'add' | 'replace' | 'delete') => {
    try {
      const { data } = await axios.post(
        `${config.API_BASE_URL}/api/bulk-venues`,
        {
          venues,
          action
        }
      );
      return data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to process bulk venues';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  // Update a venue
  const updateVenue = useCallback(async (venueId: string, updates: Partial<CreateVenueRequest>): Promise<Venue | null> => {
    if (!user) return null
    
    try {
      const { data } = await axios.put<Venue>(
        `${config.API_BASE_URL}/api/venues/${venueId}`,
        updates
      )
      
      // Refresh venues list
      await loadVenues()
      return data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update venue')
      return null
    }
  }, [user, loadVenues])

  // Delete a venue
  const deleteVenue = useCallback(async (venueId: string): Promise<void> => {
    if (!user) return
    
    try {
      await axios.delete(
        `${config.API_BASE_URL}/api/venues/${venueId}`
      )
      
      // Refresh venues list
      await loadVenues()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete venue')
    }
  }, [user, loadVenues])

  // Create a new location
  const createLocation = useCallback(async (location: CreateLocationRequest): Promise<Location | null> => {
    if (!user) return null
    
    try {
      const { data } = await axios.post<Location>(
        `${config.API_BASE_URL}/api/locations`,
        location
      )
      
      // Refresh venues list (which includes locations)
      await loadVenues()
      return data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create location')
      return null
    }
  }, [user, loadVenues])

  // Update a location
  const updateLocation = useCallback(async (locationId: string, updates: Partial<CreateLocationRequest>): Promise<Location | null> => {
    if (!user) return null
    
    try {
      const { data } = await axios.put<Location>(
        `${config.API_BASE_URL}/api/locations/${locationId}`,
        updates
      )
      
      // Refresh venues list (which includes locations)
      await loadVenues()
      return data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update location')
      return null
    }
  }, [user, loadVenues])

  // Delete a location
  const deleteLocation = useCallback(async (locationId: string): Promise<void> => {
    if (!user) return
    
    try {
      await axios.delete(
        `${config.API_BASE_URL}/api/locations/${locationId}`
      )
      
      // Refresh venues list (which includes locations)
      await loadVenues()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete location')
    }
  }, [user, loadVenues])

  // Get locations for a specific venue
  const getLocationsForVenue = useCallback((venueId: string): Location[] => {
    return locations.filter(location => location.venue_id === venueId)
  }, [locations])

  // Clear error
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const value = useMemo(
    () => ({
      venues,
      locations,
      isLoading,
      error,
      loadVenues,
      createVenue,
      updateVenue,
      deleteVenue,
      createLocation,
      updateLocation,
      deleteLocation,
      getLocationsForVenue,
      clearError,
      createOAuthClient,
      validateCSV,
      bulkVenues,
    }),
    [
      venues,
      locations,
      isLoading,
      error,
      loadVenues,
      createVenue,
      updateVenue,
      deleteVenue,
      createLocation,
      updateLocation,
      deleteLocation,
      getLocationsForVenue,
      clearError,
      createOAuthClient,
      validateCSV,
      bulkVenues,
    ]
  )

  return (
    <PortalContext.Provider value={value}>
      {children}
    </PortalContext.Provider>
  )
}
